import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Beaker, Printer, Settings as SettingsIcon, LogOut, Coins, History, Receipt, ShieldCheck, ClipboardList, Shield, BarChart3 } from "lucide-react";

const nav = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/liquids", label: "Liquides", icon: Beaker, testid: "nav-liquids" },
  { to: "/generate", label: "Générer étiquette", icon: Printer, testid: "nav-generate" },
  { to: "/history", label: "Historique", icon: History, testid: "nav-history" },
  { to: "/stats", label: "Statistiques", icon: BarChart3, testid: "nav-stats" },
  { to: "/registry", label: "Registre DGCCRF", icon: ClipboardList, testid: "nav-registry" },
  { to: "/safety-sheets", label: "Fiches de sécurité", icon: ShieldCheck, testid: "nav-safety" },
  { to: "/settings", label: "Paramètres", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/credits", label: "Crédits", icon: Coins, testid: "nav-credits" },
  { to: "/invoices", label: "Factures", icon: Receipt, testid: "nav-invoices" },
];

const adminNav = [
  { to: "/admin", label: "Administration", icon: Shield, testid: "nav-admin" },
];

export function DashboardLayout({ children, title }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-zinc-800 bg-zinc-950 flex flex-col">
        <div className="px-6 py-6 border-b border-zinc-800">
          <Link to="/dashboard" className="block" data-testid="sidebar-logo">
            <div className="font-display text-xl font-bold tracking-tight">
              mixo<span className="text-red-500"> </span>vap
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mt-1">
              Étiquetage conforme
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map(({ to, label, icon: Icon, testid }) => {
            const active = loc.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium border-l-2 transition-colors duration-150 ${
                  active
                    ? "border-red-500 bg-zinc-900 text-white"
                    : "border-transparent text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {label}
              </Link>
            );
          })}
          {user?.role === "admin" && (
            <>
              <div className="pt-3 mt-3 border-t border-zinc-800">
                <div className="px-3 py-1 text-[9px] uppercase tracking-[0.3em] text-red-500 font-mono">Admin</div>
              </div>
              {adminNav.map(({ to, label, icon: Icon, testid }) => {
                const active = loc.pathname === to;
                return (
                  <Link key={to} to={to} data-testid={testid}
                    className={`flex items-center gap-3 px-3 py-2 text-sm font-medium border-l-2 transition-colors duration-150 ${
                      active ? "border-red-500 bg-zinc-900 text-white" : "border-transparent text-zinc-400 hover:text-white hover:bg-zinc-900"
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>
        <div className="px-4 py-4 border-t border-zinc-800">
          <div className="text-xs font-mono text-zinc-500 mb-2 truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <Link to="/credits" className="flex items-center justify-between gap-2 mb-3 px-2 py-2 border border-zinc-800 hover:border-red-500/40 group" data-testid="sidebar-credits-badge">
            <span className="flex items-center gap-2">
              <Coins className="w-3.5 h-3.5 text-red-500" />
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Crédits</span>
            </span>
            <span className="font-mono text-sm text-white font-bold group-hover:text-red-500">
              {user?.role === "admin" ? "∞" : (user?.credits ?? "—")}
            </span>
          </Link>
          <button
            data-testid="sidebar-logout"
            onClick={async () => { await logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-red-500 hover:bg-zinc-900 border border-zinc-800 hover:border-red-500/40 transition-colors duration-150"
          >
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="border-b border-zinc-800 px-8 py-5 flex items-center justify-between bg-zinc-950">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              {loc.pathname}
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-1">{title}</h1>
          </div>
          <div className="text-xs font-mono text-zinc-500">
            {new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        </header>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
